import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Plane, ArrowRight, Zap, TrendingUp, ExternalLink } from 'lucide-react';

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const fmtDur = (m) => (m ? `${Math.floor(m / 60)}h ${m % 60}m` : '');

const Row = ({ label, icon: Ic, offer, tone }) => (
  <div className="flex items-center justify-between gap-2 py-1.5">
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${tone}`}><Ic className="h-3.5 w-3.5" /> {label}</span>
    <span className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{offer.airline_code} · {offer.stops === 0 ? 'nonstop' : `${offer.stops} stop`} · {fmtDur(offer.duration_minutes)}</span>
      <span className="font-semibold text-sm">{fmtINR(offer.price)}</span>
    </span>
  </div>
);

export const PopularFlights = () => {
  const [routes, setRoutes] = useState(null);
  useEffect(() => { api.popularFlights('INR').then(({ data }) => setRoutes(data.routes || [])).catch(() => setRoutes([])); }, []);

  if (routes && routes.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-popular-flights">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Real-time fares on the most-searched routes</h2>
          <p className="text-sm text-muted-foreground mt-1">Live cheapest & best-value picks, ranked by our transparent FlightDeals Score.</p>
        </div>
        <Link to="/flights" className="hidden sm:inline-flex text-accent font-semibold text-sm items-center gap-1">Search any route <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {!routes
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)
          : routes.map((r) => (
            <div key={`${r.origin}-${r.destination}`} className="rounded-2xl border border-border bg-card p-4 card-elevate" data-testid={`popular-route-${r.origin}-${r.destination}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="grid place-items-center h-8 w-8 rounded-lg bg-accent/10 text-accent"><Plane className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{r.origin_city} → {r.destination_city}</p>
                  <p className="text-[11px] text-muted-foreground">{r.origin} · {r.destination}</p>
                </div>
                {r.best?.badges?.includes('BEST VALUE') && <Badge className="ml-auto bg-accent/10 text-accent rounded-full text-[10px]">Best value</Badge>}
              </div>
              <div className="border-t border-border/60 pt-1">
                <Row label="Cheapest" icon={TrendingUp} offer={r.cheapest} tone="text-emerald-600" />
                <Row label="Best value" icon={Zap} offer={r.best} tone="text-accent" />
              </div>
              <div className="mt-3 flex gap-2">
                <a href={r.cheapest.deep_link} target="_blank" rel="sponsored noopener noreferrer" className="flex-1" data-testid={`popular-book-${r.origin}-${r.destination}`}>
                  <Button size="sm" className="w-full rounded-lg bg-accent text-accent-foreground hover:bg-accent/90">Book {fmtINR(r.cheapest.price)} <ExternalLink className="h-3.5 w-3.5 ml-1" /></Button>
                </a>
                <Link to={`/flights/results?origin=${r.origin}&destination=${r.destination}&adults=1&cabin=economy`} data-testid={`popular-compare-${r.origin}-${r.destination}`}>
                  <Button size="sm" variant="outline" className="rounded-lg">Compare</Button>
                </Link>
              </div>
            </div>
          ))}
      </div>
    </section>
  );
};
